/* eslint-disable camelcase */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';

import { usePlatformConfig } from 'contexts/PlatformConfigContext';
import { useTheme } from 'contexts/ThemeContext';
import { useUser } from 'contexts/UserContext';

import allCountries from 'utils/constants/countries';
import {
  isValidEmail,
  parseCustomLabel,
  removeDuplicates
} from 'utils/functions';
import numeral from 'utils/numeral';
import { ACTIVATION, CUSTOMERS, GROUP, TRANSACTION, USERS } from 'utils/rest';

import { ButtonCreate } from 'components/ButtonCreate';
import { Alert, Table, Text } from 'components/DataDisplay';
import { FormSection } from 'components/FormSection';
import {
  AutocompleteTextField,
  Button,
  IconButton,
  Switch,
  TextField
} from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { ListField } from 'components/ListField';
import { Loading } from 'components/Loading';
import TableField from 'components/TableField';
import { UnauthorizedModal } from 'components/UnauthorizedModal';

import { AddChildCustomer, UpdateChildCustomer } from './components/modalForm';
import { CustomerActionsTypes, useCustomer } from './contexts/customContext';
import { DeleteCustomerModal } from './components';
import { isParent, sortByDate } from './utils';

export default () => {
  const [
    { customers, countries, selectedCustomer, activableMedias },
    dispatchCustomer
  ] = useCustomer();
  const { adminRights, isSuperAdmin, user } = useUser();
  const { country } = usePlatformConfig;

  const UNAUTHORIZED = 'unauthorized';
  const [customerInfo, setCustomerInfo] = useState({});
  const [subAccounts, setSubAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchingCustomer, setFetchingCustomer] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [parents, setParents] = useState();
  const [selectedSubAccounts, setSelectedSubAccounts] = useState([]);
  const [financialAdministrators, setFinancialAdministrators] = useState([]);
  const [mandats, setMandats] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [modal, setModal] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('platform');
  const [balance, setBalance] = useState(null);
  const [isEditingBalance, setIsEditingBalance] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [budgetAlerts, setBudgetAlert] = useState(0);
  const [emailsToAlert, setEmailsToAlert] = useState([]);
  const [autoSendInvoice, setAutoSendInvoice] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [initialGroups, setInitialGroups] = useState([]);
  const [deletedIds, setDeletedIds] = useState(new Set());

  const groupsInitialized = useRef(false);
  const { themeColors } = useTheme();
  const [err, setErr] = useState({ show: false });

  const roundOffPlatformBalance = value => {
    if (typeof value !== 'number') {
      return value;
    }
    return Math.round(value * 100) / 100;
  };

  useEffect(() => {
    groupsInitialized.current = false;
    setInitialGroups([]);
    setSelectedGroups([]);
  }, [selectedCustomer?._id]);

  useEffect(() => {
    setBudgetAlert(selectedCustomer?.budgetAlerts?.budget || 0);
    setEmailsToAlert(selectedCustomer?.budgetAlerts?.emails || []);

    const formatedMandats =
      selectedCustomer?.mandats?.reduce((acc, item) => {
        const { media, mandat, startDate, endDate } = item;
        const exist = acc.find(
          el =>
            el.mandat === mandat &&
            el.startDate === startDate &&
            el.endDate === endDate
        );

        if (exist) {
          exist.medias.push({ key: media });
        } else {
          acc.push({ medias: [{ key: media }], mandat, startDate, endDate });
        }

        return acc;
      }, []) || [];

    setMandats(formatedMandats);
  }, [selectedCustomer]);

  const initContext = async () => {
    try {
      setIsLoading(true);

      const [allCustomers, allActivableMedias, allPresets] = await Promise.all([
        CUSTOMERS.get(),
        ACTIVATION.getActivableMedias(),
        ACTIVATION.getActivationPresets()
      ]);

      const customersWithGroups = [...allCustomers];

      const sortedCustomers = sortByDate(customersWithGroups);

      let localSelectedCustomer = null;
      if (sortedCustomers?.[0]?._id) {
        localSelectedCustomer = await CUSTOMERS.getStripeCustomerById(
          sortedCustomers[0]._id
        );
      }

      dispatchCustomer({
        type: CustomerActionsTypes.UPDATE_CUSTOMER,
        props: {
          presets: allPresets,
          customers: sortedCustomers,
          selectedCustomer: localSelectedCustomer,
          groups: selectedGroups,
          countries: allCountries.sort((a, b) =>
            a.label.localeCompare(b.label)
          ),
          activableMedias: allActivableMedias
        }
      });

      setCustomerInfo(localSelectedCustomer?.info || {});
      setAutoSendInvoice(localSelectedCustomer?.autoSendInvoice || false);
      setBudgetAlert(localSelectedCustomer?.budgetAlerts?.budget || 0);
      setEmailsToAlert(localSelectedCustomer?.budgetAlerts?.emails || []);
    } catch (error) {
      console.error('[initContext] Failed to initialize context:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = async value => {
    if (!value || typeof value !== 'object' || !value._id) {
      return;
    }

    try {
      const customerCheck = await CUSTOMERS.getStripeCustomerById(value._id);
      if (!customerCheck) return;

      dispatchCustomer({
        type: CustomerActionsTypes.UPDATE_CUSTOMER,
        props: { selectedCustomer: customerCheck }
      });
      setCustomerInfo(customerCheck?.info || {});
    } catch (error) {
      console.error(
        '[handleSelect] Failed to fetch selected customer details:',
        error
      );
    }
  };

  const handleModal = (right, modalKey) => {
    const isAuthorized = adminRights?.general?.customers?.[right];
    setModal(isAuthorized ? modalKey : UNAUTHORIZED);
    if (modalKey === 'customers') {
      setCustomerInfo({});
    }
  };

  const createCustomer = async () => {
    try {
      const newCustomer = await CUSTOMERS.create({
        domain: user?.domain,
        info: {
          name: '',
          address: {
            country,
            city: '',
            postalCode: '',
            line1: ''
          },
          companyId: '',
          email: ''
        },
        budgetAlerts: { emails: [], budget: 0 },
        autoSendInvoice: false
      });
      if (!newCustomer?._id) {
        console.error(
          '[createCustomer] Invalid response from server:',
          newCustomer
        );
        return;
      }
      const updatedCustomers = [...customers, newCustomer];
      dispatchCustomer({
        type: CustomerActionsTypes.UPDATE_CUSTOMER,
        props: {
          customers: updatedCustomers,
          selectedCustomer: newCustomer
        }
      });
      setCustomerInfo(newCustomer.info || {});
    } catch (error) {
      console.error('[createCustomer] Failed to create new customer:', error);
    }
  };

  const getParent = async () => {
    const base = [{ key: null, label: 'Aucun' }];

    const filteredParents = customers.reduce((acc, curr) => {
      if (!curr?.parentId && curr._id !== selectedCustomer._id) {
        acc.push({ key: curr._id, label: curr.info?.name });
      }
      return acc;
    }, base);

    const parent =
      customers.find(c => c._id === selectedCustomer.parentId) || null;
    setParents(filteredParents);
    setSelectedParent({
      key: parent?._id || null,
      label: parent?.info?.name || 'Aucun'
    });
  };

  const getSubAccounts = async () => {
    const foundSubAccounts = customers.reduce((acc, curr) => {
      if (curr.parentId === selectedCustomer._id) {
        acc.push({
          ...curr,
          id: curr._id,
          name: curr.info?.name,
          displayedMargin: curr.margin?.rate
            ? numeral(curr.margin.rate).format('0,0%')
            : 'N/A',
          subscription: curr.contribution?.default
            ? numeral(curr.contribution.default).format('0,0%')
            : 'Par défaut'
        });
      }
      return acc;
    }, []);

    const subCustommers = await Promise.all(
      foundSubAccounts.map(async customer => {
        const myBalance = await CUSTOMERS.getBalance(customer.id);
        if (customer.paymentMethod !== 'platform') {
          return {
            ...customer,
            balance: numeral(myBalance).format('0,0$')
          };
        }
        return { ...customer, balance: numeral(myBalance).format('0,0[.]00$') };
      })
    );

    setSubAccounts(subCustommers);
  };

  const getFinancialAdmins = async () => {
    if (!selectedGroups.length) {
      setAllUsers([]);
      setFinancialAdministrators([]);
      return;
    }
    try {
      const userIds = selectedGroups.reduce((acc, group) => {
        acc.push(...group.userIds);
        return acc;
      }, []);

      const uniqueUserIds = removeDuplicates(userIds);
      const users = await USERS.getByIds(uniqueUserIds);

      const administrators = uniqueUserIds.map(userId => {
        const { email, family_name, given_name, id, _id } =
          users.find(el => el._id === userId) || {};
        return { email, family_name, given_name, id, _id };
      });

      const financialAdmins = administrators.filter(administrator =>
        selectedCustomer?.financialAdmins.includes(administrator._id)
      );

      setAllUsers(administrators);
      setFinancialAdministrators(financialAdmins);
    } catch (error) {
      console.error(
        '[getFinancialAdmins] Failed to fetch users by group ID:',
        error
      );
      setAllUsers([]);
      setFinancialAdministrators([]);
    }
  };

  const getGroupCustomLabel = () => {
    if (!selectedGroups.length) {
      return;
    }
    selectedGroups
      .map(group => {
        try {
          return parseCustomLabel(group);
        } catch (error) {
          console.error(
            '[getGroupCustomLabel] Failed to parse label for group:',
            group,
            error
          );
          return '';
        }
      })
      .filter(Boolean);
  };

  const initializeCustomerData = async () => {
    if (!customers.length || !selectedCustomer) {
      return;
    }

    setFetchingCustomer(true);

    await getParent();
    await getSubAccounts();

    setAutoSendInvoice(selectedCustomer?.autoSendInvoice);

    await getFinancialAdmins();
    await getGroupCustomLabel();
    // await getPresetData();

    setFetchingCustomer(false);
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (fetchingCustomer) {
      return;
    }
    if (customers.length && selectedCustomer) {
      initializeCustomerData();
    } else {
      setCustomerInfo({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, selectedCustomer, selectedGroups, isLoading]);

  const updateCustomer = async () => {
    if (!adminRights.general?.customers?.update) {
      setModal(UNAUTHORIZED);
      return;
    }
    if (!selectedCustomer?._id) {
      console.error('[updateCustomer] No selectedCustomer to update');
      return;
    }

    const newMandats = mandats.reduce((acc, item) => {
      const { medias = [], mandat, startDate, endDate } = item;

      for (const media of medias) {
        // pour eviter les doublons de ligne
        const exist = acc.find(
          el =>
            el.media === media.key &&
            el.mandat === mandat &&
            el.startDate === startDate &&
            el.endDate === endDate
        );
        if (!exist) {
          acc.push({ media: media.key, mandat, startDate, endDate });
        }
      }

      return acc;
    }, []);

    const body = {
      ...selectedCustomer,
      parentId: selectedParent?.key,
      financialAdmins: financialAdministrators.map(admin => admin._id),
      info: customerInfo,
      offPlatformPayment: paymentMethod === 'off_platform',
      budgetAlerts: { emails: emailsToAlert, budget: budgetAlerts },
      autoSendInvoice,
      mandats: newMandats
    };

    try {
      await initializeCustomerData();
      const [customerUpdated] = await Promise.all([
        CUSTOMERS.updateCustomers(selectedCustomer._id, body)
        // ACTIVATION.updateManyFees(
        //   allFees.map(fee => ({
        //     _id: fee._id,
        //     technicalFees: fee.technicalFees / 100,
        //     setUpFees: fee.setUpFees,
        //     weeklyFees: fee.weeklyFees,
        //     dataCpm: fee.dataCpm
        //   }))
        // ),
        // ACTIVATION.updateConstraints(constraint._id, {
        //   maximumDataCost: constraint.maximumDataCost
        // })
      ]);
      if (customerUpdated?.stripeError) {
        setErr({ show: true, ...customerUpdated });
        return;
      }
      const prevIds = initialGroups.map(g => g.id);
      const newIds = selectedGroups.map(g => g.id);

      const toUnlink = initialGroups.filter(g => !newIds.includes(g.id));
      const toLink = selectedGroups.filter(g => !prevIds.includes(g.id));
      await Promise.all([
        ...toUnlink.map(g => GROUP.updateV2(g.id, { stripeCustomerId: null })),

        ...toLink.map(g =>
          GROUP.updateV2(g.id, {
            stripeCustomerId: selectedCustomer._id,
            customerName: customerInfo.name
          })
        )
      ]);
      setDeletedIds(new Set());
      const updatedCustomers = customers.map(c =>
        c._id === selectedCustomer._id
          ? { ...c, ...customerUpdated, parentId: selectedParent?.key }
          : c
      );

      dispatchCustomer({
        type: CustomerActionsTypes.UPDATE_CUSTOMER,
        props: {
          customers: updatedCustomers,
          selectedCustomer: {
            ...selectedCustomer,
            ...customerUpdated,
            parentId: selectedParent?.key,
            financialAdmins: financialAdministrators.map(admin => admin._id),
            info: customerInfo,
            offPlatformPayment: paymentMethod === 'off_platform',
            budgetAlerts: { emails: emailsToAlert, budget: budgetAlerts },
            autoSendInvoice,
            mandats
          }
        }
      });
      await initContext();
    } catch (error) {
      console.error('[updateCustomer] Failed to update customer:', error);
    }
  };

  const handleCheck = items => {
    setSelectedSubAccounts(
      items.map(id => {
        return customers.find(c => c._id === id);
      })
    );
  };

  const handleOpenUpdateChild = (rigth, name, rows) => {
    if (!adminRights.general?.customers[rigth]) {
      setModal(UNAUTHORIZED);
      return;
    }
    setSelectedSubAccounts(
      rows.map(row => {
        return customers.find(c => c._id === row.id);
      })
    );
    setModal(name);
  };

  const removeChild = async item => {
    await CUSTOMERS.updateCustomers(item.id, {
      parentId: ''
    });
    if (!adminRights?.general?.customers?.delete) {
      setModal(UNAUTHORIZED);
      return;
    }
    await CUSTOMERS.updateCustomers(item.id, {
      parentId: ''
    });

    const newCustomers = customers.map(customer => {
      if (customer.id === item._id) {
        return { ...customer, parentId: '' };
      }
      return customer;
    });

    dispatchCustomer({
      type: CustomerActionsTypes.UPDATE_CUSTOMER,
      props: {
        customers: newCustomers
      }
    });
    getSubAccounts();
  };

  const checkFields = () => {
    return (
      !customerInfo?.name ||
      !customerInfo?.address?.country ||
      !customerInfo?.companyId ||
      !customerInfo?.address?.city ||
      !customerInfo?.address?.country ||
      (customerInfo.email?.length && !isValidEmail(customerInfo.email))
    );
  };

  const deleteCustomer = async () => {
    try {
      await Promise.all(
        selectedGroups.map(group =>
          GROUP.updateV2(group._id, { stripeCustomerId: null })
        )
      );

      await CUSTOMERS.deleteCustomer(selectedCustomer._id);
      await initContext();
      setModal('');
    } catch (error) {
      console.error('[deleteCustomer] Failed to delete customer:', error);
    }
  };

  const canDelete = adminRights?.general?.customers?.advanced;

  const fetchBalance = async () => {
    if (selectedCustomer) {
      const customerBalance = await CUSTOMERS.getBalance(selectedCustomer._id);
      setBalance(customerBalance);
    }
  };

  useEffect(() => {
    fetchBalance();
    // eslint-disable-next-line
  }, [selectedCustomer, paymentMethod]);

  useEffect(() => {
    if (selectedCustomer?.offPlatformPayment) {
      setPaymentMethod('off_platform');
    } else {
      setPaymentMethod('platform');
    }
  }, [selectedCustomer]);

  const handlePaymentMethodChange = method => {
    setPaymentMethod(method.key);
  };

  const validateBalance = inputValue => {
    const inputBalance = inputValue || '';
    setNewBalance(inputBalance);
    const valid =
      !Number.isNaN(parseFloat(inputBalance)) && parseFloat(inputBalance) >= 0;
    setIsValid(valid);
  };

  const validateBudgetAlert = inputValue => {
    const inputBalance = parseFloat(inputValue) || 0;
    setBudgetAlert(inputBalance);
  };

  const handleSaveBalance = async () => {
    const updatedBalance = parseFloat(newBalance);
    if (!isValid || Number.isNaN(updatedBalance)) return;

    const transactionAmount = updatedBalance - balance;
    try {
      await TRANSACTION.create({
        stripeCustomerId: selectedCustomer._id,
        amount: transactionAmount
      });

      setBalance(updatedBalance);
      setIsEditingBalance(false);
    } catch (error) {
      console.error(
        '[handleSaveBalance] Failed to update customer balance:',
        error
      );
    }
  };

  const mediaOptions = (Array.isArray(activableMedias)
    ? activableMedias
    : []
  ).map(media => ({ key: media.key, label: media.name }));

  const organisationHeaders = [
    { id: 'id', label: 'ID' },
    { id: 'name', label: 'Nom' },
    { id: 'key', label: 'Key' },
    { id: 'actions', label: 'Actions' }
  ];

  return (
    <Loading loading={isLoading || fetchingCustomer}>
      <Container>
        {modal === UNAUTHORIZED && (
          <UnauthorizedModal open onClose={() => setModal('')} />
        )}
        {modal === 'childcustomers' && (
          <UpdateChildCustomer
            selectedSubAccounts={selectedSubAccounts}
            open
            onClose={() => {
              setModal('');
            }}
          />
        )}
        {modal === 'delete' && (
          <DeleteCustomerModal
            open
            onClose={() => setModal('')}
            onConfirm={deleteCustomer}
            title="Suppression de compte client"
            description={`Après cette manipulation, le compte client : ${selectedCustomer?.info?.name} n'existera plus et les utilisateurs ne pourront plus lancer de campagne ou d'enrichissements CRM `}
          />
        )}
        {modal === 'subaccounts' && (
          <AddChildCustomer
            open
            onClose={() => {
              setModal('');
            }}
          />
        )}
        {customers.length === 0 ? (
          <Row>
            <Item>
              <Text variant="h5" fontWeight={700}>
                Pour créer un client, cliquer sur le boutton ci-dessous
              </Text>
            </Item>
            <Item justify="center">
              <ButtonCreate onClick={() => createCustomer()}>
                Ajouter un compte
              </ButtonCreate>
            </Item>
          </Row>
        ) : (
          <>
            <Row justify="flex-end">
              <Item flex>
                <ButtonCreate onClick={() => createCustomer()}>
                  Ajouter un compte
                </ButtonCreate>
              </Item>
            </Row>
            <Row>
              <Item justify="flex-start">
                <Container>
                  <Row justify="flex-start">
                    <Item xs={4} justify="flex-start">
                      <AutocompleteTextField
                        onChange={handleSelect}
                        options={customers}
                        formatLabel={option =>
                          `${option?.info?.name} (${option.stripeId})`
                        }
                        title="Compte"
                        value={selectedCustomer}
                        renderOption={(props, item) => (
                          <li {...props} key={item.id}>
                            <Text fontSize="14px" noWrap>
                              {item?.info?.name} ({item.stripeId})
                            </Text>
                          </li>
                        )}
                      />
                    </Item>
                    <Item xs={4} justify="flex-start">
                      <AutocompleteTextField
                        onChange={e => {
                          if (e === null) {
                            setSelectedParent(null);
                          } else {
                            setSelectedParent({
                              key: e?.key,
                              label: e?.label
                            });
                          }
                        }}
                        toolTipText={
                          isParent(selectedCustomer, customers)
                            ? `${selectedCustomer?.info?.name} est dejà parent`
                            : null
                        }
                        disabled={isParent(selectedCustomer, customers)}
                        options={parents}
                        formatLabel={option =>
                          `${option.label} (${option.key})`
                        }
                        value={selectedParent}
                        title="Compte Parent"
                        renderOption={(props, item) => (
                          <li {...props} key={item.id}>
                            <Text fontSize="14px" noWrap>
                              {item?.label}
                            </Text>
                          </li>
                        )}
                      />
                    </Item>
                  </Row>
                  {selectedCustomer && (
                    <Row justify="flex-start">
                      <Item justify="flex-start">
                        <Text fontSize="14px" color="gray" fontWeight={400}>
                          Key client : <b>{selectedCustomer.key}</b>
                        </Text>
                      </Item>
                    </Row>
                  )}
                  <Row>
                    <Item xs={12}>
                      <FormSection title="Organisations liées" xs={3}>
                        <Container>
                          <Row spacing={0} justify="flex-start">
                            <Item justify="flex-start">
                              <Container>
                                <Row>
                                  <Item xs>
                                    <TableField
                                      headers={organisationHeaders}
                                      fetchPage={async ({
                                        page,
                                        limit,
                                        search
                                      }) => {
                                        const resp = await GROUP.getGroupSearch(
                                          {
                                            page,
                                            limit,
                                            search,
                                            custom: true,
                                            withFunnels: false,
                                            stripeCustomerId:
                                              selectedCustomer._id
                                          }
                                        );

                                        let apiData = resp.data.map(item => ({
                                          id: item._id || item.id,
                                          name: item.name,
                                          userIds: item.userIds,
                                          key: item.key
                                        }));

                                        apiData = apiData.filter(
                                          item => !deletedIds.has(item.id)
                                        );

                                        if (!groupsInitialized.current) {
                                          setInitialGroups(apiData);
                                          setSelectedGroups(apiData);
                                          groupsInitialized.current = true;
                                        }

                                        if (search) {
                                          return {
                                            data: apiData,
                                            totalCount:
                                              resp.totalCount || apiData.length
                                          };
                                        }

                                        const mergedData = [
                                          ...selectedGroups.filter(
                                            item => !deletedIds.has(item.id)
                                          ),
                                          ...apiData.filter(
                                            d =>
                                              !selectedGroups.some(
                                                s => s.id === d.id
                                              )
                                          )
                                        ];

                                        return {
                                          data: mergedData,
                                          totalCount:
                                            resp.totalCount || mergedData.length
                                        };
                                      }}
                                      renderRow={(row, header) => {
                                        if (header.id === 'actions') {
                                          return (
                                            <IconButton
                                              onClick={() => {
                                                setSelectedGroups(prev =>
                                                  prev.filter(
                                                    g => g.id !== row.id
                                                  )
                                                );

                                                setDeletedIds(prev =>
                                                  new Set(prev).add(row.id)
                                                );
                                              }}
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          );
                                        }
                                        return row[header.id];
                                      }}
                                      enableLinker
                                      fetchLinkerOptions={async ({
                                        search,
                                        page,
                                        limit
                                      }) => {
                                        const resp = await GROUP.getGroupSearch(
                                          {
                                            search,
                                            page,
                                            limit,
                                            custom: true
                                          }
                                        );

                                        const data = resp.data
                                          .map(item => ({
                                            id: item._id || item.id,
                                            name: item.name,
                                            key: item.key
                                          }))
                                          .filter(
                                            item =>
                                              !selectedGroups.some(
                                                g => g.id === item.id
                                              ) && !deletedIds.has(item.id)
                                          );
                                        return {
                                          data,
                                          hasMore: data.length === limit
                                        };
                                      }}
                                      onLinkerSelect={item => {
                                        setSelectedGroups(prev => [
                                          item,
                                          ...prev
                                        ]);

                                        setDeletedIds(prev => {
                                          const newSet = new Set(prev);
                                          newSet.delete(item.id);
                                          return newSet;
                                        });
                                      }}
                                      renderLinkerOption={item =>
                                        `${item.name} (${item.id})`
                                      }
                                    />
                                  </Item>
                                </Row>
                              </Container>
                            </Item>
                          </Row>
                        </Container>
                      </FormSection>
                    </Item>
                  </Row>
                </Container>
              </Item>
            </Row>

            <Row>
              <Item justify="flex-start">
                <FormSection title="Informations de facturation" xs={3}>
                  <Container>
                    <Row justify="flex-start">
                      <Item xs={4} justify="flex-start">
                        <AutocompleteTextField
                          disabled={!isSuperAdmin}
                          required
                          options={[
                            { key: 'platform', label: 'Paiement plateforme' },
                            {
                              key: 'off_platform',
                              label: 'Paiement hors plateforme'
                            }
                          ]}
                          formatLabel={option => option.label}
                          value={
                            paymentMethod === 'platform'
                              ? {
                                  key: 'platform',
                                  label: 'Paiement plateforme'
                                }
                              : {
                                  key: 'off_platform',
                                  label: 'Paiement hors plateforme'
                                }
                          }
                          onChange={handlePaymentMethodChange}
                          title="Type de paiement"
                        />
                      </Item>
                      <Item xs={4} justify="flex-start">
                        <Box display="flex" alignItems="center">
                          {isEditingBalance &&
                          paymentMethod === 'off_platform' ? (
                            <TextField
                              value={roundOffPlatformBalance(newBalance)}
                              format="numberDec"
                              unit="€"
                              onChange={value => {
                                validateBalance(value);
                              }}
                              title="Modifier le solde"
                            />
                          ) : (
                            <TextField
                              value={roundOffPlatformBalance(balance)}
                              placeholder="Chargement..."
                              title="Solde"
                              unit="€"
                              format="numberDec"
                              disabled={
                                balance === null ||
                                paymentMethod === 'platform' ||
                                !isEditingBalance
                              }
                            />
                          )}
                          {paymentMethod === 'off_platform' && isSuperAdmin && (
                            <IconButton
                              onClick={() => {
                                if (isEditingBalance) {
                                  handleSaveBalance();
                                } else {
                                  setNewBalance(balance);
                                  setIsEditingBalance(true);
                                }
                              }}
                            >
                              {isEditingBalance ? (
                                <CheckIcon
                                  style={{
                                    color: isValid
                                      ? themeColors.themeV2.backoffice
                                      : themeColors.themeV2.notValidated,
                                    transform: 'translateY(5px)'
                                  }}
                                />
                              ) : (
                                <EditIcon
                                  style={{
                                    transform: 'translateY(5px)'
                                  }}
                                />
                              )}
                            </IconButton>
                          )}
                        </Box>
                      </Item>
                    </Row>
                    <Row justify="flex-start">
                      <Item xs={4} justify="flex-start">
                        <TextField
                          required
                          error={!customerInfo?.companyId}
                          value={customerInfo?.companyId || ''}
                          onChange={e => {
                            setCustomerInfo({
                              ...customerInfo,
                              companyId: e
                            });
                          }}
                          title="SIRET"
                        />
                      </Item>
                      <Item xs={4} justify="flex-start">
                        <AutocompleteTextField
                          required
                          onChange={e => {
                            setCustomerInfo({
                              ...customerInfo,
                              address: {
                                ...customerInfo.address,
                                country: e.key
                              }
                            });
                          }}
                          error={!customerInfo?.address?.country}
                          options={countries}
                          formatLabel={option => option?.label}
                          value={
                            countries.find(
                              c =>
                                c.key.toLowerCase() ===
                                customerInfo?.address?.country.toLowerCase()
                            ) ||
                            countries.find(c =>
                              c.label
                                .toLowerCase()
                                .includes(
                                  customerInfo?.address?.country.toLowerCase()
                                )
                            ) ||
                            null
                          }
                          title="Pays ou région"
                        />
                      </Item>
                    </Row>
                    <Row justify="flex-start">
                      <Item xs={4} justify="flex-start">
                        <TextField
                          required
                          error={!customerInfo?.name}
                          value={customerInfo?.name || ''}
                          onChange={e => {
                            setCustomerInfo({
                              ...customerInfo,
                              name: e
                            });
                          }}
                          title="Raison sociale"
                        />
                      </Item>
                      <Item xs={4} justify="flex-start">
                        <TextField
                          required
                          error={!customerInfo?.address?.line1}
                          value={customerInfo?.address?.line1 || ''}
                          onChange={e => {
                            setCustomerInfo({
                              ...customerInfo,
                              address: {
                                ...customerInfo.address,
                                line1: e
                              }
                            });
                          }}
                          title="Adresse (ligne 1)"
                        />
                      </Item>
                    </Row>
                    <Row justify="flex-start">
                      <Item xs={4} justify="flex-start">
                        <TextField
                          value={customerInfo?.address?.line2 || ''}
                          onChange={e => {
                            setCustomerInfo({
                              ...customerInfo,
                              address: {
                                ...customerInfo.address,
                                line2: e
                              }
                            });
                          }}
                          title="Adresse (ligne 2)"
                        />
                      </Item>
                      <Item xs={4} justify="flex-start">
                        <TextField
                          value={customerInfo?.address?.postalCode || ''}
                          onChange={e => {
                            setCustomerInfo({
                              ...customerInfo,
                              address: {
                                ...customerInfo.address,
                                postalCode: e
                              }
                            });
                          }}
                          title="Code postal"
                        />
                      </Item>
                    </Row>
                    <Row justify="flex-start">
                      <Item xs={4} justify="flex-start">
                        <TextField
                          error={!customerInfo?.address?.city}
                          value={customerInfo?.address?.city || ''}
                          onChange={e => {
                            setCustomerInfo({
                              ...customerInfo,
                              address: {
                                ...customerInfo.address,
                                city: e
                              }
                            });
                          }}
                          title="Ville"
                          required
                        />
                      </Item>
                      <Item xs={4} justify="flex-start">
                        <TextField
                          value={customerInfo?.address?.state || ''}
                          onChange={e => {
                            setCustomerInfo({
                              ...customerInfo,
                              address: {
                                ...customerInfo.address,
                                state: e
                              }
                            });
                          }}
                          title="Région"
                        />
                      </Item>
                    </Row>
                    <Row justify="flex-start">
                      <Item xs={4} justify="flex-start">
                        <TextField
                          value={customerInfo?.euVatNumber || ''}
                          onChange={e => {
                            setCustomerInfo({
                              ...customerInfo,
                              euVatNumber: e
                            });
                          }}
                          title="Numéro de TVA intracommunautaire"
                        />
                      </Item>
                      <Item xs={4} justify="flex-start">
                        <TextField
                          value={customerInfo?.email || ''}
                          onChange={e => {
                            setCustomerInfo({
                              ...customerInfo,
                              email: e
                            });
                          }}
                          title="Email de facturation"
                        />
                      </Item>
                    </Row>
                    <Row justify="flex-start">
                      <Item xs={4} justify="flex-start">
                        <Switch
                          onChange={setAutoSendInvoice}
                          checked={autoSendInvoice}
                          color={themeColors.primary}
                          title="Envoi automatique de facture"
                        />
                      </Item>
                      <Item xs={4} justify="flex-start">
                        <Switch
                          onChange={e => {
                            dispatchCustomer({
                              type: CustomerActionsTypes.UPDATE_CUSTOMER,
                              props: {
                                selectedCustomer: {
                                  ...selectedCustomer,
                                  activablePurchaseOrder: e
                                }
                              }
                            });
                          }}
                          disabled={
                            typeof selectedCustomer?.activablePurchaseOrder !==
                            'boolean'
                          }
                          checked={selectedCustomer?.activablePurchaseOrder}
                          color={themeColors.primary}
                          title="Bon de commande"
                        />
                      </Item>
                    </Row>
                  </Container>
                </FormSection>
              </Item>
            </Row>
            <Row>
              <Item>
                <FormSection title="Alertes de budgets" xs={3}>
                  <Container>
                    <Row justify="flex-start">
                      <Item xs={2} justify="flex-start">
                        <Switch
                          value={selectedCustomer?.budgetAlerting}
                          onChange={e => {
                            dispatchCustomer({
                              type: CustomerActionsTypes.UPDATE_CUSTOMER,
                              props: {
                                selectedCustomer: {
                                  ...selectedCustomer,
                                  budgetAlerting: e
                                }
                              }
                            });
                          }}
                          checked={selectedCustomer?.budgetAlerting}
                          color={themeColors.primary}
                          title="Alerting"
                          options={['Non', 'Oui']}
                        />
                      </Item>
                      {selectedCustomer?.budgetAlerting && (
                        <Item xs={3} justify="flex-start">
                          <TextField
                            value={selectedCustomer?.budgetAlerts?.budget}
                            format="numberDec"
                            unit="€"
                            onChange={value => {
                              validateBudgetAlert(value);
                            }}
                            title="Si solde inférieur ou égal à "
                            required
                          />
                        </Item>
                      )}
                      {selectedCustomer?.budgetAlerting && (
                        <Row justify="flex-start">
                          <Item xs={4} justify="flex-start">
                            <ListField
                              values={
                                selectedCustomer?.budgetAlerts?.emails?.map(
                                  el => ({ email: el })
                                ) || []
                              }
                              onChange={newValues => {
                                setEmailsToAlert([
                                  ...newValues
                                    .filter(el => el?.email)
                                    .map(el => el.email)
                                ]);
                              }}
                              actionTxt="Prévenir"
                              deleteAction
                              variant="customFields"
                              inputs={['TextField']}
                              labels={['Prévenir cet email']}
                              options={['email']}
                            />
                          </Item>
                        </Row>
                      )}
                    </Row>
                  </Container>
                </FormSection>
              </Item>
            </Row>
            <Row>
              <Item>
                <FormSection title="Administrateurs financiers" xs={3}>
                  <Container>
                    <Item xs>
                      <ListField
                        options={allUsers}
                        searchOptionsKeys={['email']}
                        hideKeys={['_id', 'id']}
                        values={financialAdministrators}
                        onChange={setFinancialAdministrators}
                        actionTxt="Ajouter un administrateur"
                        selectNbMax={1}
                        deleteAction
                      />
                    </Item>
                  </Container>
                </FormSection>
              </Item>
            </Row>
            <Row>
              <Item>
                <FormSection title="Mandats de paiement" xs={3}>
                  <Container>
                    <Row>
                      <Item justify="flex-start">
                        <Text fontSize="14px" fontWeight={400}>
                          Sélectionnez ci-dessous les médias pour lesquels Ermes
                          / Redpill est non payeur. Par défaut, si rien
                          paramétré, alors nous sommes considérés comme payeur
                          sur le média . L’achat d’espace des médias “non
                          payeurs” ne sera pas prélevé pour lancer une campagne.
                          Seuls les frais techniques, data et les frais de setup
                          seront prélevés.
                        </Text>
                      </Item>
                    </Row>
                    <Row spacing={0} justify="flex-start">
                      <Item justify="flex-start">
                        <ListField
                          values={mandats}
                          onChange={setMandats}
                          actionTxt="Ajouter un mandat"
                          deleteAction
                          variant="customFields"
                          inputs={[
                            'MultiSelect',
                            'Select',
                            'DatePicker',
                            'DatePicker'
                          ]}
                          isRequireds={[true, true, false, false]}
                          labels={[
                            'Media',
                            'Payeur',
                            'date de début',
                            'date de fin'
                          ]}
                          options={['medias', 'mandat', 'startDate', 'endDate']}
                          fieldsFormat={[
                            mediaOptions,
                            [
                              { label: 'Redpill payeur', value: true },
                              { label: 'Client payeur', value: false }
                            ]
                          ]}
                        />
                      </Item>
                    </Row>
                  </Container>
                </FormSection>
              </Item>
            </Row>
            <Row>
              {(isParent(selectedCustomer, customers) ||
                !selectedCustomer?.parentId) && (
                <Item>
                  <FormSection title="Sous-comptes" xs={3}>
                    <Container>
                      <Row>
                        <Item justify="flex-start">
                          <Text fontSize="14px" fontWeight={400}>
                            Sélectionnez ci-dessous les sous-comptes auxquels
                            vous voulez appliquer un prélèvement et de
                            l’abondement.
                          </Text>
                        </Item>
                      </Row>
                      <Row>
                        <Item xs>
                          <ButtonCreate
                            onClick={() => handleModal('create', 'subaccounts')}
                            justify="flex-start"
                          >
                            Ajouter un compte
                          </ButtonCreate>
                        </Item>
                      </Row>
                      <Row>
                        <Item>
                          {subAccounts.length > 0 && (
                            <Table
                              headers={[
                                {
                                  id: 'name',
                                  label: 'Sous-compte'
                                },
                                {
                                  id: 'balance',
                                  label: 'Solde'
                                },
                                {
                                  id: 'subscription',
                                  label: 'Abondement',
                                  help:
                                    "L'abondement s'applique uniquement sur les campagnes prêtes à l'emploi"
                                },
                                {
                                  id: 'displayedMargin',
                                  label: 'Prélévement'
                                },
                                {
                                  id: 'actions',
                                  label: (
                                    <Button
                                      variant="contained"
                                      color={themeColors.themeV2.backoffice}
                                      onClick={() =>
                                        handleModal('update', 'childcustomers')
                                      }
                                      disabled={!selectedSubAccounts.length}
                                    >
                                      <Text color="inherit">
                                        Modifier le prélèvement et
                                        l&apos;abondement
                                      </Text>
                                    </Button>
                                  )
                                }
                              ]}
                              showCheckboxes
                              rows={subAccounts}
                              skipSlice
                              actions={[
                                {
                                  key: 'edit',
                                  icon: <EditIcon />,
                                  onClick: row =>
                                    handleOpenUpdateChild(
                                      'update',
                                      'childcustomers',
                                      [row]
                                    ),
                                  display: () => true
                                },
                                {
                                  key: 'delete',
                                  icon: <DeleteIcon />,
                                  onClick: row => removeChild(row),
                                  display: () => true
                                }
                              ]}
                              onCheck={handleCheck}
                            />
                          )}
                        </Item>
                      </Row>
                    </Container>
                  </FormSection>
                </Item>
              )}
            </Row>
            <Row>
              <Item xs justify="flex-end">
                <Button
                  color={themeColors.black}
                  onClick={updateCustomer}
                  variant="contained"
                  disabled={checkFields()}
                  disabledMessage="L’un des champs requis n’est pas renseigné"
                >
                  <SaveIcon />
                  &nbsp; Sauvegarder
                </Button>
              </Item>
              {canDelete && (
                <Item xs={6} justify="flex-start">
                  <Button
                    variant="contained"
                    color={themeColors.red}
                    onClick={() => setModal('delete')}
                  >
                    <DeleteIcon /> Supprimer
                  </Button>
                </Item>
              )}
            </Row>
            {err.show && (
              <Row>
                <Item>
                  <Alert
                    severity="error"
                    text={err.message}
                    onClose={() => {
                      setErr({ show: !err.show, ...err });
                    }}
                  />
                </Item>
              </Row>
            )}
          </>
        )}
      </Container>
    </Loading>
  );
};
